/**
 * Step 3: Customer Segment
 * Form fields for customer targeting: Area, Tier selection, Whitelist upload, and Customer List
 */

import { useState } from "react";
import { Select } from "@/components/ui/select";
import { FileUpload } from "@/components/ui/file-upload";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Pagination,
} from "@/components/ui/table/table";
import type {
  ProgramFormData,
  TierType,
  CustomerData,
} from "@/feature/programs/domain/model";

interface CustomerSegmentStepProps {
  formData: ProgramFormData;
  errors: Partial<Record<keyof ProgramFormData, string>>;
  onInputChange: (field: keyof ProgramFormData, value: string) => void;
  onTierChange: (tier: TierType, checked: boolean) => void;
  onFileChange: (fileName: string, file?: File | null) => void;
  whitelistFileError?: string | null;
  isUploading?: boolean;
}

const areaOptions = [
  { value: "", label: "Select Area", disabled: true },
  { value: "all", label: "All" },
  { value: "jabodetabek", label: "Jabodetabek" },
  { value: "jawa-barat", label: "Jawa Barat" },
  { value: "jawa-tengah", label: "Jawa Tengah" },
  { value: "jawa-timur", label: "Jawa Timur" },
  { value: "sumatera", label: "Sumatera" },
  { value: "kalimantan", label: "Kalimantan" },
  { value: "sulawesi", label: "Sulawesi" },
];

const tierOptions: { value: TierType; label: string }[] = [
  { value: "bronze", label: "Bronze" },
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "platinum", label: "Platinum" },
];

export const CustomerSegmentStep = ({
  formData,
  errors,
  onInputChange,
  onTierChange,
  onFileChange,
  whitelistFileError,
  isUploading = false,
}: CustomerSegmentStepProps): React.JSX.Element => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter customers by search query
  const filteredCustomers = formData.customerList.filter(
    (customer: CustomerData) =>
      customer.customerId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.area.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.tier.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / itemsPerPage));
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Row 1: Area and Tier */}
      <div className="grid grid-cols-2 gap-6">
        {/* Area */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="area"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Area
          </label>
          <Select
            id="area"
            value={formData.area}
            onChange={(e) => onInputChange("area", e.target.value)}
            options={areaOptions}
            errorMessage={errors.area}
          />
        </div>

        {/* Tier Checkboxes */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-gray-800 text-left">
            Tier
          </label>
          <div className="flex gap-3">
            {tierOptions.map((tier) => (
              <label
                key={tier.value}
                className={`
                  flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer
                  ${formData.tiers.includes(tier.value)
                    ? "border-[#001A41] bg-white"
                    : "border-gray-300 bg-white"
                  }
                `}
              >
                <input
                  type="checkbox"
                  checked={formData.tiers.includes(tier.value)}
                  onChange={(e) => onTierChange(tier.value, e.target.checked)}
                  className="w-4 h-4 text-[#001A41] border-gray-300 rounded focus:ring-[#001A41]"
                />
                <span className="text-sm text-gray-800">{tier.label}</span>
              </label>
            ))}
          </div>
          {errors.tiers && (
            <span className="text-xs text-red-600">{errors.tiers}</span>
          )}
        </div>
      </div>
      {/* Row 2: Whitelist File Upload */}
      <div className="flex flex-col gap-1.5 max-w-[560px]">
        <label className="text-sm font-semibold text-gray-800 text-left">
          Whitelist <span className="font-normal text-gray-500">Optional</span>
        </label>
        {isUploading ? (
          <div className="flex items-center justify-center h-24 border border-dashed border-gray-300 rounded-lg bg-gray-50">
            <div className="flex items-center gap-2 text-gray-500">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Uploading file...</span>
            </div>
          </div>
        ) : (
          <FileUpload
            id="whitelist"
            value={formData.whitelistFile}
            onChange={(fileName, file) => onFileChange(fileName, file)}
            accept=".xlsx,.xls,.csv"
            acceptText="File type: .xlsx, .csv"
            maxSizeText="Maximum size: 5MB"
            variant="document"
            showIcon={true}
            errorMessage={whitelistFileError ?? undefined}
          />
        )}
        {formData.whitelistTotalCount > 0 && (
          <span className="text-sm text-gray-600">
            Total customers: {formData.whitelistTotalCount.toLocaleString()}
          </span>
        )}
      </div>

      {/* Customer List Section */}
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-bold text-gray-900 text-left">Customer List</h3>

        {/* Search */}
        <div className="w-64">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search"
          />
        </div>

        {/* Table */}
        <Table>
          <TableHeader>
            <tr>
              <TableHead>Customer ID</TableHead>
              <TableHead>Phone Number</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Area</TableHead>
              <TableHead>Tier</TableHead>
            </tr>
          </TableHeader>
          <TableBody>
            {paginatedCustomers.length > 0 ? (
              paginatedCustomers.map((customer: CustomerData) => (
                <TableRow key={customer.customerId}>
                  <TableCell>{customer.customerId}</TableCell>
                  <TableCell>{customer.phoneNumber}</TableCell>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{customer.area}</TableCell>
                  <TableCell>{customer.tier}</TableCell>
                </TableRow>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-500">
                  No Data
                </td>
              </tr>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

